import { useMemo } from 'react'

import { Spinner } from '@wartek-id/spinner'

import {
  useAPIContentKinerjaUtama,
  useAPIContentPerilakuKerja,
} from 'api/kinerja/useAPIKinerja'
import { ContentContext } from 'app/KinerjaPage/hooks/useContentContext'

import type {
  ContentPerilakuKerja,
  Jenjang,
  KinerjaPraktikPembelajaranContentIndikator,
  KinerjaPraktikPembelajaranDimensi,
  KinerjaTambahanDetail,
  KinerjaUtamaDetail,
  PerilakuKerjaAspek,
  PerilakuKerjaEkpektasi,
  PerilakuKerjaIndikator,
} from 'types/kinerja'

interface ContentContextProps {
  jenjang: Jenjang
  children: React.ReactNode
  ptkId?: string
}

export default function ContentProvider({
  jenjang,
  children,
  ptkId,
}: ContentContextProps) {
  const {
    data: {
      data: {
        kinerjaUtama: contentKinerjaUtama = [],
        praktikPembelajaran: contentPraktikPembelajaran = [],
        pengembanganKompetensi: contentPengembanganKompetensi = [],
        tugasTambahan: contentTugasTambahan = [],
        isRaporExists,
      } = {},
    } = {},
    isFetched: isContentKinerjaUtamaFetched,
  } = useAPIContentKinerjaUtama({
    jenjang,
    ptkId,
  })
  const {
    data: { data: contentPerilakuKerja } = {},
    isFetched: isContentPerilakuKerjaFetched,
  } = useAPIContentPerilakuKerja({ ptkId })

  const sortByOrder = (
    a: KinerjaUtamaDetail | KinerjaTambahanDetail,
    b: KinerjaUtamaDetail | KinerjaTambahanDetail
  ) => a.order - b.order
  const sortAspek = (a: PerilakuKerjaAspek, b: PerilakuKerjaAspek) => {
    return a.orderAspek - b.orderAspek
  }
  const sortIndikator = (
    a: PerilakuKerjaIndikator,
    b: PerilakuKerjaIndikator
  ) => a.orderIndikator - b.orderIndikator
  const sortEkspektasi = (
    a: PerilakuKerjaEkpektasi,
    b: PerilakuKerjaEkpektasi
  ) => a.orderEkspektasi - b.orderEkspektasi

  const sortContentPerilakuKerja = (
    contentPerilakuKerja: ContentPerilakuKerja
  ): ContentPerilakuKerja => {
    return {
      aspek: contentPerilakuKerja.aspek.sort(sortAspek).map((aspek) => ({
        ...aspek,
        indikator: aspek.indikator.sort(sortIndikator).map((indikator) => ({
          ...indikator,
          ekspektasi: indikator.ekspektasi.sort(sortEkspektasi),
        })),
      })),
    }
  }

  // create function to sort contentPraktikPembelajaran by dimensiId,
  // which is an arbitrary string
  const sortContentPraktikPembelajaran = (
    contentPraktikPembelajaran: KinerjaPraktikPembelajaranDimensi[]
  ): KinerjaPraktikPembelajaranDimensi[] => {
    return contentPraktikPembelajaran.sort((a, b) => {
      const aId = a.dimensiId
      const bId = b.dimensiId

      if (aId < bId) {
        return -1
      }

      if (aId > bId) {
        return 1
      }

      return 0
    })
  }

  const allIndikatorPraktikPembelajaran = useMemo(
    () => contentPraktikPembelajaran.flatMap((dimensi) => dimensi.indikator),
    [contentPraktikPembelajaran]
  )

  const getPraktikPembelajaranById = (
    indikatorId: KinerjaPraktikPembelajaranContentIndikator['id']
  ): KinerjaPraktikPembelajaranContentIndikator => {
    return allIndikatorPraktikPembelajaran.find(
      (indikator) => indikator.id === indikatorId
    )
  }

  const getPengembanganKompetensiById = (
    id: KinerjaUtamaDetail['id']
  ): KinerjaUtamaDetail => {
    return contentPengembanganKompetensi.find((kinerja) => kinerja.id === id)
  }

  const getKinerjaUtamaById = (
    id: KinerjaUtamaDetail['id']
  ): KinerjaUtamaDetail => {
    return contentKinerjaUtama.find((kinerja) => kinerja.id === id)
  }

  const getTugasTambahanById = (
    id: KinerjaUtamaDetail['id']
  ): KinerjaUtamaDetail => {
    return contentTugasTambahan.find((kinerja) => kinerja.id === id)
  }

  const getAspekPerilakuKerjaById = (
    id: PerilakuKerjaAspek['id']
  ): PerilakuKerjaAspek => {
    return contentPerilakuKerja.aspek.find((aspek) => aspek.id === id)
  }

  return !(isContentKinerjaUtamaFetched && isContentPerilakuKerjaFetched) ? (
    <div className="flex p-10">
      <div className="m-auto">
        <Spinner data-testid="loading-spinner-content" />
      </div>
    </div>
  ) : (
    <ContentContext.Provider
      value={{
        contentKinerjaUtama: contentKinerjaUtama.sort(sortByOrder),
        contentPerilakuKerja: sortContentPerilakuKerja(contentPerilakuKerja),
        contentPraktikPembelajaran: sortContentPraktikPembelajaran(
          contentPraktikPembelajaran
        ),
        contentPengembanganKompetensi,
        contentTugasTambahan,
        isRaporExists,
        getPraktikPembelajaranById,
        getPengembanganKompetensiById,
        getKinerjaUtamaById,
        getTugasTambahanById,
        getAspekPerilakuKerjaById,
      }}
    >
      {children}
    </ContentContext.Provider>
  )
}
